import { Drawer } from '@base-ui/react/drawer';
import styled from '@emotion/styled';
import { alpha } from '@mui/system';

import { focusRing, hoverable, pressable } from '@/design-system';
import { UserIconWrapper } from '@/shared/design-system/components/UserDropdown/UserDropdown.styled';

export const MobileActionButton = styled('button')(({ theme }) => ({
  ...hoverable(theme, 'surfaceRaised'),
  width: 44,
  height: 44,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  borderRadius: theme.radius[8],
  backgroundColor: theme.colors.background.raised,
  color: theme.colors.text.primary,
  cursor: 'pointer',

  '& svg': {
    width: 18,
    height: 18,
    color: 'inherit',
  },
}));

export const MobileUserButton = styled('button')(({ theme }) => ({
  ...hoverable(theme, 'surfaceRaised'),
  ...pressable(theme, 'surfaceRaised'),
  ...focusRing(theme),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 32,
  height: 32,
  padding: 0,
  overflow: 'hidden',
  border: 'none',
  borderRadius: theme.radius.full,
  backgroundColor: theme.colors.background.overlay,
  color: theme.colors.icon.primary,
  cursor: 'pointer',

  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  '& svg': { width: 16, height: 16, color: 'inherit' },
}));

// ---- Bottom sheet (User Account Drawer) ----

export const BottomSheetBackdrop = styled(Drawer.Backdrop)(({ theme }) => ({
  position: 'fixed',
  inset: 0,
  zIndex: 1300,
  backgroundColor: alpha(theme.colors.black[100], 0.5),

  '&[data-starting-style], &[data-ending-style]': {
    opacity: 0,
  },

  transition: 'opacity 0.3s ease',
}));

export const BottomSheetPopup = styled(Drawer.Popup)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 1400,
  backgroundColor: theme.colors.background.raised,
  border: `1px solid ${alpha(theme.colors.text.primary, 0.15)}`,
  borderBottom: 'none',
  borderRadius: '12px 12px 0 0',
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  maxHeight: '92dvh',
  overflowY: 'auto',

  transform: 'translateY(calc(100% + var(--drawer-swipe-movement-y, 0px)))',
  transition: 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',

  '&[data-open]': {
    transform: 'translateY(calc(var(--drawer-swipe-movement-y, 0px)))',
  },

  '&[data-starting-style], &[data-ending-style]': {
    transform: 'translateY(100%)',
  },

  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

// ---- Side Nav Drawer ----

export const NavDrawerBackdrop = styled(Drawer.Backdrop)(({ theme }) => ({
  position: 'fixed',
  inset: 0,
  zIndex: 1300,
  backgroundColor: alpha(theme.colors.black[100], 0.5),

  '&[data-starting-style], &[data-ending-style]': {
    opacity: 0,
  },

  transition: 'opacity 0.3s ease',
}));

export const NavDrawerPopup = styled(Drawer.Popup)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  zIndex: 1400,
  width: '85vw',
  maxWidth: 320,
  backgroundColor: theme.colors.background.canvas,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',

  transform: 'translateX(calc(100% + var(--drawer-swipe-movement-x, 0px)))',
  transition: 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',

  '&[data-open]': {
    transform: 'translateX(calc(var(--drawer-swipe-movement-x, 0px)))',
  },

  '&[data-starting-style], &[data-ending-style]': {
    transform: 'translateX(100%)',
  },

  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

export const NavDrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 4,
  padding: '10px 12px',
  borderBottom: `1px solid ${theme.colors.border.subtle}`,
  flexShrink: 0,
}));

export const NavDrawerUserRow = styled('button')(({ theme }) => ({
  ...hoverable(theme),
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  background: 'none',
  border: 'none',
  borderRadius: theme.radius[8],
  cursor: 'pointer',
  padding: 0,
  color: theme.colors.text.primary,
  textAlign: 'left',
  minWidth: 0,
}));

// Extends UserIconWrapper with larger size for the nav drawer
export const NavDrawerUserAvatar = styled(UserIconWrapper)({
  width: 36,
  height: 36,
  borderRadius: 8,

  svg: {
    width: 18,
    height: 18,
  },
});

export const NavDrawerQRButton = styled('button')(({ theme }) => ({
  ...hoverable(theme),
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '12px 16px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: theme.colors.text.primary,
  width: '100%',
  textAlign: 'left',
  borderBottom: `1px solid ${theme.colors.border.default}`,
  flexShrink: 0,

  svg: {
    width: 18,
    height: 18,
    color: 'inherit',
  },
}));

export const NavDrawerContent = styled('div')({
  flex: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',

  '&::-webkit-scrollbar': {
    display: 'none',
  },
});
